import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { TextField, ToggleField } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  Chip,
  ConfirmModal,
  FormActions,
  RemoveRowButton,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { queryErrorMessage } from '~/modules/query';
import {
  useCreateAttributeMutation,
  useDeleteAttributeMutation,
  useUpdateAttributeMutation,
} from '~/modules/query/featureFlags';

import {
  ATTRIBUTE_TYPES,
  ATTR_KEY_RE,
  OPERATORS,
  OPERATOR_LABELS,
  isRequiredAttribute,
} from '~/components/Constants/featureFlagsConstants';
import { Select, errorText, isStale, toOptions } from '~/components/pages/feature-flags/common';
import type { Attribute, AttributeInput, Operator } from '~/components/pages/feature-flags/types';
import { useReference } from '~/components/pages/feature-flags/useReference';
import { routes } from '~/app/routes';

interface Draft {
  key: string;
  type: Attribute['type'];
  operators: Operator[];
  allowedValues: string[];
  notes: string;
}

const FORM_ID = 'attribute-form';
const LIST = routes.featureFlagsAdmin('attributes');

const toDraft = (a: Attribute | null): Draft => ({
  key: a?.key ?? '',
  type: a?.type ?? 'string',
  operators: a?.operators ?? ['in', 'notIn'],
  allowedValues: a?.allowedValues ?? [],
  notes: a?.notes ?? '',
});

const toPayload = (d: Draft): AttributeInput => ({
  key: d.key.trim(),
  type: d.type,
  // A boolean is checked with "is true" or "is false", stored as "in"; the service enforces the same.
  operators: d.type === 'boolean' ? ['in'] : OPERATORS.filter((op) => d.operators.includes(op)),
  allowedValues:
    d.type === 'enum' && d.key.trim() !== 'product' ? d.allowedValues.map((v) => v.trim()) : [],
  notes: d.notes.trim() || null,
});

const valuesProblem = (p: AttributeInput): string | undefined => {
  if (p.type !== 'enum' || p.key === 'product') return undefined;
  const values = p.allowedValues ?? [];
  if (values.length === 0) return 'Add at least one allowed value.';
  if (values.some((v) => !v || v.length > 128 || v.includes(',')))
    return 'Each allowed value needs 1 to 128 characters and no commas.';
  if (new Set(values).size !== values.length) return 'Each allowed value must be unique.';
  return undefined;
};

/** Create (`/feature-flags/admin/attributes/new`) and edit (`/feature-flags/admin/attributes/:id`) one attribute. The subtree is behind the feature flags access gate (FeatureFlag purview). */
export default function AttributePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const ref = useReference();
  const { products, attributes } = ref.reference;
  const record = attributes.find((a) => a.key === id);
  const notFound = ref.loaded && !isNew && !record && !ref.reference.pending;

  // A save (or someone else's) bumps `version`, which reseeds; a plain refetch keeps unsaved edits.
  const { draft, setDraft, patch, dirty, reset, markSaved } = useRecordDraft<
    Attribute | null,
    Draft
  >(ref.loaded ? (isNew ? null : record) : undefined, {
    id: id ?? 'new',
    version: record?.version,
    toDraft,
  });
  const guard = useUnsavedGuard(dirty);

  const [deleting, setDeleting] = useState(false);
  const [createAttribute] = useCreateAttributeMutation();
  const [updateAttribute] = useUpdateAttributeMutation();
  const [deleteAttribute] = useDeleteAttributeMutation();

  const schema = useMemo(
    () =>
      z
        .object({
          key: isNew
            ? z
                .string()
                .trim()
                .regex(
                  ATTR_KEY_RE,
                  'Start with a letter; use only letters, numbers and underscores (40 max).',
                )
            : z.string(),
          operators: z.array(z.string()),
          allowedValues: z.array(z.string()),
          type: z.string(),
          notes: z.string(),
        })
        .superRefine((d, ctx) => {
          const payload = toPayload(d as Draft);
          if (payload.operators.length === 0)
            ctx.addIssue({
              code: 'custom',
              path: ['operators'],
              message: 'Allow at least one operator.',
            });
          const problem = valuesProblem(payload);
          if (problem) ctx.addIssue({ code: 'custom', path: ['allowedValues'], message: problem });
        }),
    [isNew],
  );
  const check = useFormValidation(schema, draft, (field) =>
    field === 'operators'
      ? 'attribute-operators-error'
      : field === 'allowedValues'
        ? 'attribute-values-error'
        : `attribute-${field}`,
  );

  const save = useRecordSave<Draft>({
    save: async (d) => {
      const payload = toPayload(d);
      if (isNew) await createAttribute(payload).unwrap();
      else if (record)
        await updateAttribute({
          key: record.key,
          data: { ...payload, version: record.version },
        }).unwrap();
    },
    success: isNew ? 'Attribute created' : 'Attribute saved',
    failure: 'The attribute could not be saved. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !check.submit()) return;
    if (!(await save.submit(draft))) return;
    if (!isNew) return markSaved(draft);
    guard.release();
    navigate(routes.featureFlagsAdminRecord('attributes', draft.key.trim()), { replace: true });
  };

  const remove = () => {
    if (!record) return;
    deleteAttribute({ key: record.key, version: record.version })
      .unwrap()
      .then(() => {
        toast.success('Attribute deleted');
        guard.release();
        navigate(LIST, { replace: true });
      })
      .catch((err) =>
        toast.error(
          isStale(err)
            ? 'This attribute changed. Reload before deleting.'
            : errorText(
                err,
                'The attribute could not be deleted. It may still be used by targeting rules.',
              ),
        ),
      );
    setDeleting(false);
  };

  const title = isNew ? 'Create attribute' : (record?.key ?? 'Attribute');
  const failed = !ref.loaded && !!ref.error;
  const isProduct = draft?.key.trim() === 'product';
  const availableOps = OPERATORS.filter((op) => draft?.type !== 'enum' || op !== 'endsWith');
  const fieldError = (id: string, message?: string) =>
    message && (
      <p id={id} tabIndex={-1} role="alert" className="text-sm text-error-600 dark:text-error-400">
        {message}
      </p>
    );

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'Feature flags', href: routes.featureFlags },
        { label: 'Admin' },
        { label: 'Attributes', href: LIST },
        { label: title },
      ]}
      actions={
        record && !isRequiredAttribute(record.key)
          ? [
              {
                label: 'Delete attribute',
                kind: 'danger',
                testId: 'attribute-delete',
                startIcon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setDeleting(true),
              },
            ]
          : undefined
      }
      loading={!draft}
      notFound={notFound}
      notFoundTitle="Attribute not found"
      backTo={LIST}
      backLabel="Back to attributes"
      error={failed ? { what: 'the attribute', message: queryErrorMessage(ref.error) } : null}
      onRetry={ref.refetch}
      guard={guard}
      testId="attribute"
    >
      {draft && (
        <form id={FORM_ID} noValidate onSubmit={submit}>
          <Card>
            <CardBody className="space-y-6">
              {save.error && <Alert variant="error" message={save.error} />}
              {record && isRequiredAttribute(record.key) && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This is an identity attribute every SDK context carries, so it cannot be deleted.
                </p>
              )}
              <CardSection title="Details">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <TextField
                    id="attribute-key"
                    testId="attribute-key"
                    label="Key"
                    required
                    code
                    readOnly={!isNew}
                    value={draft.key}
                    onChange={(key) => patch({ key })}
                    onBlur={() => check.touch('key')}
                    error={check.errors.key}
                    placeholder="planLevel"
                    helperText={
                      isNew ? 'Keys are permanent.' : 'Keys cannot change after creation.'
                    }
                  />
                  <Select
                    id="attribute-type"
                    label="Type"
                    value={draft.type}
                    options={toOptions(ATTRIBUTE_TYPES)}
                    onChange={(v) =>
                      setDraft((d) => ({
                        ...d,
                        type: v as Attribute['type'],
                        operators:
                          v === 'enum'
                            ? d.operators.filter((op) => op !== 'endsWith')
                            : d.operators,
                      }))
                    }
                  />
                  <div className="sm:col-span-2">
                    <TextField
                      id="attribute-notes"
                      testId="attribute-notes"
                      label="Notes"
                      value={draft.notes}
                      onChange={(notes) => patch({ notes })}
                      helperText="Optional guidance for whoever writes rules."
                    />
                  </div>
                </div>
              </CardSection>

              {/* A boolean has no operator choice: rules check it with "is true" or "is false". */}
              {draft.type !== 'boolean' && (
                <CardSection
                  title="Allowed operators"
                  description="Rules can only use the operators ticked here. Removing one that a rule uses is refused."
                >
                  <div className="space-y-3">
                    {fieldError('attribute-operators-error', check.errors.operators)}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {availableOps.map((op) => (
                        <ToggleField
                          key={op}
                          id={`attribute-op-${op}`}
                          testId={`attribute-op-${op}`}
                          inline
                          text={OPERATOR_LABELS[op]}
                          checked={draft.operators.includes(op)}
                          onChange={(on) =>
                            patch({
                              operators: on
                                ? [...draft.operators, op]
                                : draft.operators.filter((x) => x !== op),
                            })
                          }
                        />
                      ))}
                    </div>
                  </div>
                </CardSection>
              )}

              {draft.type === 'enum' && (
                <CardSection
                  title="Allowed values"
                  description="Add each value separately. Audience rules offer these exact choices."
                >
                  {isProduct ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Product values come from the Products page and cannot be changed here.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {products.map((p) => (
                          <Chip key={p.key} label={p.key} />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {fieldError('attribute-values-error', check.errors.allowedValues)}
                      {draft.allowedValues.map((value, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <div className="flex-1">
                            <TextField
                              id={`attribute-value-${index}`}
                              testId={`attribute-value-${index}`}
                              label={`Allowed value ${index + 1}`}
                              value={value}
                              onChange={(v) =>
                                patch({
                                  allowedValues: draft.allowedValues.map((x, i) =>
                                    i === index ? v : x,
                                  ),
                                })
                              }
                            />
                          </div>
                          <div className="pt-7">
                            <RemoveRowButton
                              label={`Remove allowed value ${index + 1}`}
                              onClick={() =>
                                patch({
                                  allowedValues: draft.allowedValues.filter((_, i) => i !== index),
                                })
                              }
                              testId={`attribute-value-${index}-remove`}
                            />
                          </div>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        startIcon={<Plus className="size-4" aria-hidden="true" />}
                        disabled={draft.allowedValues.length >= 100}
                        onClick={() => patch({ allowedValues: [...draft.allowedValues, ''] })}
                        testId="attribute-add-value"
                      >
                        Add value
                      </Button>
                    </div>
                  )}
                </CardSection>
              )}
            </CardBody>
            <CardFooter>
              <FormActions
                form={FORM_ID}
                {...(isNew
                  ? {
                      discardHref: LIST,
                      discardLabel: 'Cancel',
                      saveLabel: 'Create attribute',
                      busyLabel: 'Creating…',
                    }
                  : {
                      onDiscard: () => {
                        reset();
                        check.reset();
                        save.clearError();
                      },
                      disabled: !dirty,
                      disabledReason: 'No changes to save',
                    })}
                busy={save.busy}
                saveTestId="attribute-save"
              />
            </CardFooter>
          </Card>
        </form>
      )}

      <ConfirmModal
        isOpen={deleting}
        title="Delete attribute"
        message={`Delete attribute '${record?.key}'?`}
        confirmLabel="Delete attribute"
        variant="danger"
        dangerNote="The attribute is removed for good, so rules can no longer target it. Apps that still send this value are unaffected. An attribute that rules still use cannot be deleted."
        testId="attribute-delete-confirm"
        onClose={() => setDeleting(false)}
        onConfirm={remove}
      />
    </RecordPageShell>
  );
}
